import {Component, computed, effect, inject, Signal, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {ModelFieldSelectInclusionComponent} from '../../../../../../shared/components/inputs/model-field/model-field-select-inclusion/model-field-select-inclusion.component';
import {CardModule} from 'primeng/card';
import {ToastModule} from 'primeng/toast';
import {translate, TranslocoService} from '@jsverse/transloco';
import {DesignCriteria, DirectusTranslation, DirectusTranslationPipe} from '@frontend/directus';
import {ProjectStepBaseReactive} from '../../../project-base-step/project-base-step-reactive.component';
import {DesignCriteriaService} from './step-2-1-design-criteria.service';
import {
  AuditInfo,
  DesignCriteriaResponse,
  ProjectRoleType,
  ProjectStepType,
  UpsertDesignCriteriaRequest,
} from '@frontend/shared-api';
import {ToastService} from '@frontend/toast';
import {firstValueFrom} from 'rxjs';
import {BadgeListComponent} from '../../../../../../shared/components/list/badge-list/badge-list.component';
import {BadgeListItem} from '../../../../../../shared/components/list/badge-list/badge-list.types';
import {StepDotMenuComponent} from '../../../../../../shared/components/step-dot-menu/step-dot-menu.component';
import {CardTitleComponent} from '../../../../../../shared/components/header/card-title/card-title.component';
import {AuditInfoComponent} from '../../../../../../shared/components/audit-info/audit-info.component';
import {latestAuditInfo} from '../../../../../../shared/utils/audit-info.utils';
import {LoadingComponent} from '../../../../../../shared/components/loading/loading.component';

interface CriteriaItem {
  id?: number;
  directusId?: string;
  key: string;
  title: string;
  description: string;
  isIncluded: boolean | null;
  translations: DirectusTranslation[];
  directusData?: DesignCriteria;
}

interface CriteriaState {
  items: CriteriaItem[];
  loading: boolean;
  saving: Set<string>;
}

@Component({
  selector: 'app-step-2-1-design-criteria',
  imports: [
    FormsModule,
    ModelFieldSelectInclusionComponent,
    CardModule,
    ToastModule,
    DirectusTranslationPipe,
    BadgeListComponent,
    StepDotMenuComponent,
    CardTitleComponent,
    AuditInfoComponent,
    LoadingComponent,
  ],
  templateUrl: './step-2-1-design-criteria.component.html',
})
export class Step21DesignCriteriaComponent extends ProjectStepBaseReactive {
  protected readonly ProjectRoleType = ProjectRoleType;
  protected readonly ProjectStepType = ProjectStepType;

  private readonly service = inject(DesignCriteriaService);
  private readonly toast = inject(ToastService);
  private readonly transloco = inject(TranslocoService);
  protected readonly translate = translate;

  currentLang = this.transloco.getActiveLang();

  state = signal<CriteriaState>({
    items: [],
    loading: true,
    saving: new Set<string>(),
  });

  private readonly originalDirectusCriteria = signal<DesignCriteria[]>([]);

  protected readonly auditInfo = signal<AuditInfo | null>(null);

  badgeListItems: Signal<BadgeListItem[]> = computed(() =>
    this.state().items.map((item: CriteriaItem) => {
      const identifier = 'DC' + (item.directusData?.dcNumber ?? '');
      return {
        key: item.key,
        identifier: identifier,
        translations: item.translations,
        originalItem: item,
      };
    }),
  );
  loading = computed<boolean>(() => this.isLoadingState() || this.state().loading);

  isCriteriaDisabled = (key: string) => this.state().saving.has(key);

  constructor() {
    super();

    effect(() => {
      const projectId = this.projectId();
      if (projectId != null) {
        this.loadData(projectId);
      }
    });
  }

  private loadData(projectId: number): void {
    this.updateState({loading: true});

    this.service.getDesignCriteria(projectId).subscribe({
      next: ({criteria, selections}) => {
        this.originalDirectusCriteria.set(criteria);
        this.auditInfo.set(latestAuditInfo(selections.map((selection) => selection.auditInfo)));

        const items = this.buildCriteriaItems(criteria, selections);
        this.updateState({items, loading: false});
      },
      error: () => {
        this.updateState({loading: false});
      },
    });
  }

  private buildCriteriaItems(
    criteria: DesignCriteria[],
    selections: DesignCriteriaResponse[],
  ): CriteriaItem[] {
    const directusItems = criteria.map((dc) => {
      const saved = selections.find((s) => s.directusId === dc.id);

      return {
        id: saved?.id,
        directusId: dc.id,
        key: dc.id,
        title: dc.title,
        description: '',
        isIncluded: saved?.included ?? null,
        translations: dc.translations,
        directusData: dc,
      };
    });

    const customItems = selections
      .filter((s) => !s.directusId)
      .map((s) => ({
        id: s.id,
        directusId: undefined,
        key: String(s.id),
        title: s.name ?? '',
        description: s.description ?? '',
        isIncluded: s.included,
        translations: this.buildCustomCriteriaTranslations(s),
      }));

    return [...directusItems, ...customItems];
  }

  // almost 99% is dead code. and create a new criteria is not supported in the UI. However, keep for now. due to is not part of this story
  // all DB tables name and description are null and they actually SHOULD be removed!
  private buildCustomCriteriaTranslations(
    selection: DesignCriteriaResponse,
  ): DirectusTranslation[] {
    return ['de', 'en'].map((language) => ({
      id: selection.id,
      relationship_id: String(selection.id),
      language,
      name: selection.name ?? 'NAME in USE; Expected to be dead code',
      description: selection.description ?? '',
    }));
  }

  async onIncludeToggle(key: string, isIncluded: boolean): Promise<void> {
    const item = this.state().items.find((i) => i.key === key);
    if (!item) {
      console.error('Criteria item not found for toggle:', key);
      return;
    }
    const prev = item.isIncluded;
    this.updateItemByKey(key, {isIncluded});

    try {
      await this.saveCriteria(key, isIncluded);
    } catch {
      this.updateItemByKey(key, {isIncluded: prev});
    }
  }

  private updateItemByKey(key: string, changes: Partial<CriteriaItem>): number {
    const items = this.state().items;
    const idx = items.findIndex((i) => i.key === key);
    if (idx === -1) {
      console.error('Criteria item not found:', key);
      return -1;
    }
    const updated = [...items];
    updated[idx] = {...updated[idx], ...changes};
    this.updateState({items: updated});
    return idx;
  }

  private updateState(partial: Partial<CriteriaState>): void {
    this.state.update((cur) => ({...cur, ...partial}));
  }

  private async saveCriteria(key: string, included: boolean): Promise<void> {
    const projectId = this.projectId()!;

    const item = this.state().items.find((i) => i.key === key);
    if (!item) {
      console.error('Criteria item not found for save:', key);
      return;
    }
    this.addToSaving(key);

    try {
      const req: UpsertDesignCriteriaRequest = {
        included,
        ...(item.id != null && {id: item.id}),
        ...(item.directusId != null
          ? {directusId: item.directusId}
          : {name: item.title, description: item.description}),
      };

      const saved: DesignCriteriaResponse = await firstValueFrom(
        this.service.upsertDesignCriteria(projectId, req),
      );

      this.updateItemByKey(key, {
        id: saved.id,
        directusId: saved.directusId ?? item.directusId,
      });
      this.auditInfo.set(saved.auditInfo ?? null);

      await this.toast.showSuccess('common.success', 'project.steps.sectionSaved');
    } finally {
      this.removeFromSaving(key);
      await this.updateProjectStatus();
    }
  }

  private addToSaving(key: string): void {
    const set = new Set(this.state().saving);
    set.add(key);
    this.updateState({saving: set});
  }

  private removeFromSaving(key: string): void {
    const set = new Set(this.state().saving);
    set.delete(key);
    this.updateState({saving: set});
  }

  private async updateProjectStatus(): Promise<void> {
    try {
      await this.stepState.loadStepStatuses(this.projectId()!);
    } catch (error) {
      console.error('Error updating step statuses:', error);
      throw error;
    }
  }
}
